import React, { useState, useMemo, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiFilter, FiX } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { InquiriesFilters } from './InquiriesFilters';
import type { InquiryStatusFilter } from './InquiriesFilters';
import { InquiryCard } from './InquiryCard';
import { InquiryDetailPanel } from './InquiryDetailPanel';
import { UpdateInquiryStatusModal } from './UpdateInquiryStatusModal';
import { SendInquiryMailModal } from './SendInquiryMailModal';
import { ContactMethodModal } from './ContactMethodModal';
import { EmptyState } from '../../../ui/EmptyState';
import type { DateRange } from '../common/DateRangePicker';
import type { InquiryItem } from '../types';
import {
  useGetServiceApplications,
  useGetServiceApplication,
  useUpdateServiceApplication,
  useGetMyServices,
  type ServiceApplicationListItem,
  type ServiceApplicationsFilterParams,
  type ServiceApplicationStatus,
} from '../../../../api/services';
import {
  useGetConversationMessages,
  useOpenConversation,
  useGetConversationDetail,
} from '../../../../api/conversations';
import { useAuth } from '../../../../hooks/useAuth';
import { useConversationWs } from '../../../../hooks/useConversationWs';
import { toast } from '../../../../utils/toast';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimeAgo(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatReceivedAt(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function mapApiStatusToInquiryStatus(
  apiStatus: string,
): 'new' | 'replied' | 'offer_sent' | 'closed' {
  if (apiStatus === 'new') return 'new';
  if (apiStatus === 'in_conversation' || apiStatus === 'replied') return 'replied';
  if (apiStatus === 'contracted' || apiStatus === 'offer_sent') return 'offer_sent';
  return 'closed';
}

const CardSkeleton: React.FC = () => (
  <div className="rounded-2xl border border-[#2C2C2E] bg-[#1C1C1E] p-4 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-full bg-white/5" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 rounded bg-white/5" />
        <div className="h-3 w-24 rounded bg-white/5" />
      </div>
    </div>
    <div className="mt-3 h-10 rounded-xl bg-white/5" />
  </div>
);

// ─── Component ──────────────────────────────────────────────────────────────

export const InquiriesTab: React.FC = () => {
  const { user } = useAuth();
  const currentUserId = user?.id ? Number(user.id) : Number(localStorage.getItem('user_id') || 0);
  const navigate = useNavigate();

  // Filters state
  const [statusFilter, setStatusFilter] = useState<InquiryStatusFilter>('All');
  const [dateRange, setDateRange] = useState<DateRange>({ preset: 'Any date' });
  const [selectedService, setSelectedService] = useState('All');
  const [selectedInquiryUuid, setSelectedInquiryUuid] = useState<string | undefined>();
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Modals state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [showContactChoiceModal, setShowContactChoiceModal] = useState(false);
  const [targetContactInquiry, setTargetContactInquiry] = useState<InquiryItem | null>(null);

  // Conversation Resolution State
  const openConversation = useOpenConversation();
  const [resolvedConversationUuid, setResolvedConversationUuid] = useState<string | null>(null);

  // ── API Queries ───────────────────────────────────────────────────────────

  const apiFilterParams = useMemo((): ServiceApplicationsFilterParams => {
    const params: ServiceApplicationsFilterParams = {};
    if (statusFilter === 'new') params.status = 'new';
    if (statusFilter === 'replied') params.status = 'in_conversation';
    if (statusFilter === 'contracted') params.status = 'contracted';
    if (statusFilter === 'closed') params.status = 'closed';

    if (dateRange.preset === 'Today') params.date_preset = 'today';
    else if (dateRange.preset === 'This week') params.date_preset = 'this_week';
    else if (dateRange.preset === 'This month') params.date_preset = 'this_month';
    else if (dateRange.preset === 'Custom' && dateRange.startDate && dateRange.endDate) {
      params.date_preset = 'custom';
      params.start_date = dateRange.startDate;
      params.end_date = dateRange.endDate;
    }
    return params;
  }, [statusFilter, dateRange]);

  const {
    data: inquiriesData,
    isLoading: listLoading,
    isError: listError,
  } = useGetServiceApplications(apiFilterParams);

  const { data: myServices = [] } = useGetMyServices();

  const { data: inquiryDetail, isLoading: detailLoading } =
    useGetServiceApplication(selectedInquiryUuid);

  const updateInquiry = useUpdateServiceApplication();

  // ── Data Processing ───────────────────────────────────────────────────────

  const rawInquiries = useMemo(() => inquiriesData?.data?.results ?? [], [inquiriesData]);

  const servicesList = useMemo(() => {
    return myServices.map((s) => s.title).filter(Boolean);
  }, [myServices]);

  const mappedInquiries = useMemo((): InquiryItem[] => {
    return rawInquiries.map((app: ServiceApplicationListItem) => {
      const email = app.applicant_email || 'guest@email.com';
      const guestName = email.split('@')[0] || 'Guest Inquiry';
      const rawSource = (app as any).source || (app as any).inquiry_source || (app as any).referrer;

      return {
        id: app.application_uuid,
        guestName: guestName === 'guest' ? 'Guest Inquiry' : guestName,
        guestEmail: email,
        avatarColor: 'bg-indigo-600',
        status: mapApiStatusToInquiryStatus(app.status),
        interestedIn: app.service?.title || 'General Service',
        source: rawSource,
        message: 'No preview message available',
        timestamp: formatTimeAgo(app.created_at),
        receivedAt: formatReceivedAt(app.created_at),
        applicantId:
          (app as any).applicant?.id ||
          (app as any).applicant_id ||
          (app as any).user_id ||
          (app as any).user?.id,
        serviceId: app.service?.id,
      };
    });
  }, [rawInquiries]);

  const filteredInquiries = useMemo(() => {
    return mappedInquiries.filter((inq) => {
      if (selectedService !== 'All' && inq.interestedIn !== selectedService) return false;
      return true;
    });
  }, [mappedInquiries, selectedService]);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sync selected inquiry with filtered list
  useEffect(() => {
    if (!selectedInquiryUuid && filteredInquiries.length > 0) {
      setSelectedInquiryUuid(filteredInquiries[0].id);
    }
    if (selectedInquiryUuid && !filteredInquiries.some((i) => i.id === selectedInquiryUuid)) {
      setSelectedInquiryUuid(filteredInquiries[0]?.id);
    }
  }, [filteredInquiries, selectedInquiryUuid]);

  // Escape key deselect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedInquiryUuid(undefined);
        setShowMobileSheet(false);
        setShowStatusModal(false);
        setShowContactChoiceModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const selectedInquiry = useMemo((): InquiryItem | null => {
    if (!inquiryDetail) {
      return filteredInquiries.find((i) => i.id === selectedInquiryUuid) || null;
    }
    const email = inquiryDetail.applicant_email || 'guest@email.com';
    const guestName = email.split('@')[0] || 'Guest Inquiry';
    const rawSource =
      (inquiryDetail as any).source ||
      (inquiryDetail as any).inquiry_source ||
      (inquiryDetail as any).referrer;

    return {
      id: inquiryDetail.application_uuid,
      guestName: guestName === 'guest' ? 'Guest Inquiry' : guestName,
      guestEmail: email,
      avatarColor: 'bg-indigo-600',
      status: mapApiStatusToInquiryStatus(inquiryDetail.status),
      interestedIn: inquiryDetail.service?.title || 'General Service',
      source: rawSource,
      message: inquiryDetail.message || 'No description provided.',
      timestamp: formatTimeAgo(inquiryDetail.created_at),
      receivedAt: formatReceivedAt(inquiryDetail.created_at),
      applicantId:
        (inquiryDetail as any).applicant?.id ||
        (inquiryDetail as any).applicant_id ||
        (inquiryDetail as any).user_id ||
        (inquiryDetail as any).user?.id,
      applicantAvatar:
        (inquiryDetail as any).applicant?.profile_image ||
        (inquiryDetail as any).applicant?.avatar ||
        (inquiryDetail as any).applicant?.profile_picture,
      serviceId: inquiryDetail.service?.id,
    };
  }, [inquiryDetail, filteredInquiries, selectedInquiryUuid]);

  // Resolve conversation when selected inquiry changes
  useEffect(() => {
    const applicantId = selectedInquiry?.applicantId;
    const serviceId = selectedInquiry?.serviceId;

    if (!selectedInquiryUuid || !applicantId || !serviceId) {
      setResolvedConversationUuid(null);
      return;
    }

    const resolve = async () => {
      try {
        const conv = await openConversation.mutateAsync({
          recipient_id: applicantId,
          context_type: 'service',
          context_id: serviceId.toString(),
        });
        if (conv?.conversation_uuid) {
          setResolvedConversationUuid(conv.conversation_uuid);
        }
      } catch (err) {
        console.error('Failed to resolve conversation for inquiry:', err);
      }
    };

    resolve();
  }, [selectedInquiryUuid, selectedInquiry?.applicantId, selectedInquiry?.serviceId]);

  // Real-time socket for inquiry conversation messages
  useConversationWs(resolvedConversationUuid || undefined);

  // Query conversation messages
  const { data: infiniteData } = useGetConversationMessages(resolvedConversationUuid || undefined, {
    refetchInterval: false,
  });

  const liveMessages = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page: any) => {
      if (Array.isArray(page)) return page;
      if (Array.isArray(page?.messages)) return page.messages;
      if (Array.isArray(page?.data)) return page.data;
      if (Array.isArray(page?.results)) return page.results;
      if (Array.isArray(page?.data?.results)) return page.data.results;
      return [];
    });
  }, [infiniteData]);

  // Query conversation details to resolve applicant/other participant details
  const { data: conversationDetail } = useGetConversationDetail(
    resolvedConversationUuid || undefined,
  );

  // Resolve applicant avatar url dynamically
  const applicantAvatarUrl = useMemo(() => {
    const otherParticipant = conversationDetail?.other_participant;
    return (
      otherParticipant?.profile_picture ||
      otherParticipant?.prof_pics ||
      otherParticipant?.google_prof_pics ||
      selectedInquiry?.applicantAvatar
    );
  }, [conversationDetail, selectedInquiry]);

  // Map messages to Replies structure
  const conversationReplies = useMemo(() => {
    const sorted = [...liveMessages].sort(
      (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
    );

    return sorted.map((msg) => ({
      id: msg.id,
      senderName: msg.sender_id === currentUserId ? 'You' : selectedInquiry?.guestName || 'Guest',
      senderAvatar:
        msg.sender_id === currentUserId
          ? (user as any)?.profPics ||
            (user as any)?.profile_image ||
            (user as any)?.avatar ||
            undefined
          : msg.sender_avatar || applicantAvatarUrl,
      isUser: msg.sender_id === currentUserId,
      text: msg.body,
      textHtml: msg.body_html || undefined,
      timestamp: formatReceivedAt(msg.created_at),
    }));
  }, [liveMessages, currentUserId, selectedInquiry, user, applicantAvatarUrl]);

  // Map attachments dynamically from liveMessages
  const conversationAttachments = useMemo(() => {
    const list: { name: string; url: string }[] = [];
    liveMessages.forEach((msg) => {
      if (msg.attachments && msg.attachments.length > 0) {
        msg.attachments.forEach((att: any) => {
          const name =
            att.original_filename || att.name || att.url?.split('/').pop() || 'Attachment';
          const url = att.url || '#';
          list.push({ name, url });
        });
      }
    });
    return list;
  }, [liveMessages]);

  // Map previous channel dynamically
  const previousChannel = useMemo((): 'email' | 'chat' | undefined => {
    if (!liveMessages || liveMessages.length === 0) return undefined;
    const lastMsg = liveMessages[liveMessages.length - 1];
    return lastMsg.body_html ? 'email' : 'chat';
  }, [liveMessages]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleUpdateStatusConfirm = (newStatus: ServiceApplicationStatus) => {
    if (!selectedInquiryUuid) return;
    updateInquiry.mutate({
      applicationUuid: selectedInquiryUuid,
      status: newStatus,
    });
    setShowStatusModal(false);
  };

  const handleCloseInquiryDirect = () => {
    if (!selectedInquiryUuid) return;
    updateInquiry.mutate({
      applicationUuid: selectedInquiryUuid,
      status: 'closed',
    });
    toast.success('Inquiry marked as closed.');
  };

  const handleOpenReplyFlow = (inq?: InquiryItem | null) => {
    const target = inq || selectedInquiry;
    if (target) {
      setTargetContactInquiry(target);
      setShowContactChoiceModal(true);
    }
  };

  return (
    <div>
      {/* Filters bar (normal flow, non-sticky) */}
      <InquiriesFilters
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        selectedService={selectedService}
        onServiceChange={setSelectedService}
        servicesList={servicesList}
      />

      {/* Main Grid container */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Inquiries List */}
        <section className="space-y-3 lg:col-span-5">
          {listLoading && (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          )}

          {listError && !listLoading && (
            <div className="rounded-2xl border border-[#2C2C2E] bg-[#1C1C1E] p-8 text-center text-sm text-zinc-400">
              Failed to load inquiries.
            </div>
          )}

          {!listLoading && !listError && (
            <>
              {filteredInquiries.length === 0 ? (
                <EmptyState
                  size="compact"
                  icon={<FiFilter size={18} />}
                  title="No inquiries found"
                  description="No inquiries match your filters. Try a different status or service."
                  className="rounded-2xl border border-[#2C2C2E] bg-[#1C1C1E]"
                />
              ) : (
                filteredInquiries.map((inq) => {
                  return (
                    <InquiryCard
                      key={inq.id}
                      inquiry={inq}
                      isSelected={inq.id === selectedInquiryUuid}
                      onSelect={() => {
                        setSelectedInquiryUuid(inq.id);
                        setShowMobileSheet(true);
                      }}
                      onReply={() => handleOpenReplyFlow(inq)}
                    />
                  );
                })
              )}
            </>
          )}
        </section>

        {/* Right Column: Sticky Detail Panel Content (Desktop) */}
        <aside className="hidden lg:block lg:col-span-7">
          <div
            className={`sticky top-[calc(var(--header-height,5.5rem)+1.25rem)] transition-all duration-300 flex flex-col min-h-0 ${
              isScrolled
                ? 'h-[calc(100vh-var(--header-height,5.5rem)-3rem)]'
                : 'h-[calc(100vh-17.5rem)] min-h-[460px]'
            }`}
          >
            {detailLoading ? (
              <div className="h-full rounded-3xl border border-[#2C2C2E] bg-[#1C1C1E] p-6 animate-pulse space-y-4">
                <div className="h-12 w-12 rounded-full bg-white/5" />
                <div className="h-6 w-48 rounded bg-white/5" />
                <div className="h-10 rounded-2xl bg-white/5" />
                <div className="h-32 rounded-2xl bg-white/5" />
              </div>
            ) : (
              <InquiryDetailPanel
                inquiry={
                  selectedInquiry
                    ? { ...selectedInquiry, applicantAvatar: applicantAvatarUrl }
                    : null
                }
                onPickFirst={
                  filteredInquiries.length > 0
                    ? () => setSelectedInquiryUuid(filteredInquiries[0].id)
                    : undefined
                }
                onOpenReply={() => handleOpenReplyFlow(selectedInquiry)}
                onOpenSendMail={() => {
                  setTargetContactInquiry(selectedInquiry);
                  setShowSendMailModal(true);
                }}
                onOpenChat={() => {
                  if (resolvedConversationUuid) {
                    navigate({
                      to: '/messages',
                      search: { conversationUuid: resolvedConversationUuid },
                    });
                  } else {
                    toast.error('Conversation is not yet resolved.');
                  }
                }}
                onOpenUpdateStatus={() => setShowStatusModal(true)}
                onCloseInquiry={handleCloseInquiryDirect}
                conversationReplies={conversationReplies}
                attachments={conversationAttachments}
              />
            )}
          </div>
        </aside>
      </div>

      {/* Mobile Bottom Sheet Drawer (< lg) */}
      <AnimatePresence>
        {showMobileSheet && selectedInquiryUuid && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm lg:hidden"
            onClick={() => setShowMobileSheet(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-0 left-0 right-0 flex h-[88vh] max-h-[88vh] flex-col rounded-t-3xl border-t border-[#2C2C2E] bg-[#1C1C1E] p-4 sm:p-6 shadow-2xl overflow-hidden min-h-0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between border-b border-[#2C2C2E] pb-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-10 rounded-full bg-zinc-500" />
                  <span className="text-sm font-bold text-white">Inquiry Details</span>
                </div>
                <button
                  onClick={() => setShowMobileSheet(false)}
                  className="rounded-full p-2 text-zinc-400 hover:bg-white/10 hover:text-white"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
                <InquiryDetailPanel
                  inquiry={
                    selectedInquiry
                      ? { ...selectedInquiry, applicantAvatar: applicantAvatarUrl }
                      : null
                  }
                  onPickFirst={
                    filteredInquiries.length > 0
                      ? () => setSelectedInquiryUuid(filteredInquiries[0].id)
                      : undefined
                  }
                  onOpenReply={() => handleOpenReplyFlow(selectedInquiry)}
                  onOpenSendMail={() => {
                    setTargetContactInquiry(selectedInquiry);
                    setShowSendMailModal(true);
                  }}
                  onOpenChat={() => {
                    if (resolvedConversationUuid) {
                      navigate({
                        to: '/messages',
                        search: { conversationUuid: resolvedConversationUuid },
                      });
                    } else {
                      toast.error('Conversation is not yet resolved.');
                    }
                  }}
                  onOpenUpdateStatus={() => setShowStatusModal(true)}
                  onCloseInquiry={handleCloseInquiryDirect}
                  conversationReplies={conversationReplies}
                  attachments={conversationAttachments}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Contact Channel Choice Modal (Mail vs Chat) ───────────────────────── */}
      {showContactChoiceModal && (targetContactInquiry || selectedInquiry) && (
        <ContactMethodModal
          guestName={(targetContactInquiry || selectedInquiry)!.guestName}
          guestEmail={(targetContactInquiry || selectedInquiry)!.guestEmail}
          previousChannel={previousChannel}
          isGuest={!(targetContactInquiry || selectedInquiry)?.applicantId}
          onSelectEmail={() => {
            setShowContactChoiceModal(false);
            setShowSendMailModal(true);
          }}
          onSelectChat={() => {
            setShowContactChoiceModal(false);
            if (resolvedConversationUuid) {
              navigate({
                to: '/messages',
                search: { conversationUuid: resolvedConversationUuid },
              });
            } else {
              toast.error('Conversation is not yet resolved.');
            }
          }}
          onClose={() => setShowContactChoiceModal(false)}
        />
      )}

      {/* ── Update Inquiry Status Modal ─────────────────────────────────────── */}
      {showStatusModal && selectedInquiry && (
        <UpdateInquiryStatusModal
          guestName={selectedInquiry.guestName}
          currentStatus={selectedInquiry.status}
          onConfirm={handleUpdateStatusConfirm}
          onCancel={() => setShowStatusModal(false)}
        />
      )}

      {/* ── Send Mail Modal ─────────────────────────────────────────────────── */}
      {showSendMailModal && (targetContactInquiry || selectedInquiry) && (
        <SendInquiryMailModal
          serviceId={inquiryDetail?.service?.id || 0}
          applicationUuid={(targetContactInquiry || selectedInquiry)!.id}
          guestName={(targetContactInquiry || selectedInquiry)!.guestName}
          guestEmail={(targetContactInquiry || selectedInquiry)!.guestEmail}
          serviceTitle={(targetContactInquiry || selectedInquiry)!.interestedIn}
          conversationUuid={resolvedConversationUuid || ''}
          onClose={() => setShowSendMailModal(false)}
        />
      )}
    </div>
  );
};
